import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  Divider,
  FormControl,
  InputBase,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ListCheckBox } from "../generals/ListCheckBox";
import { useTranslation } from "react-i18next";
import { useSendNotifications } from "../../data/notifications/hooks";
import Swal from "sweetalert2";

export const SendNotifications = ({
  formValues,
  handleInputChange,
  userList,
  userWithActivePosition,
}) => {
  const { t } = useTranslation("global");
  const { clubFilter, isActiveFilter, message } = formValues;

  const [checked, setChecked] = useState(userList);
  const [addUsers, setAddUsers] = useState([]);
  const [otherUsers, setOtherUsers] = useState([]);

  const clubsList = [...new Set(userList.map((user) => user.club))];

  const isActive =
    isActiveFilter === "Activos"
      ? true
      : isActiveFilter === "Posición activa"
      ? false
      : null;

  const filteredUsers = useMemo(() => {
    let filtered = [...userList];

    if (isActiveFilter === "Activos") {
      filtered = filtered.filter((user) => user.isActive === isActive);
    } else if (isActiveFilter === "Posición activa") {
      filtered = filtered.filter((user) =>
        userWithActivePosition.some((activeUser) => activeUser.id === user.id)
      );
    }

    if (clubFilter[0] !== "Todos los clubs") {
      filtered = filtered.filter((user) => clubFilter.includes(user.club));
    }

    return filtered;
  }, [userList, isActiveFilter, clubFilter, isActive, userWithActivePosition]);

  useEffect(() => {
    if (isActiveFilter !== "Todos los socios") {
      const newChecked = checked?.filter((user) => user.isActive === isActive);
      setChecked(newChecked);
    }

    if (clubFilter !== "Todos los clubs") {
      const newChecked = checked?.filter((user) => user.club === clubFilter);
      setChecked(newChecked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveFilter, clubFilter]);

  const { error, response, handleSendNotifications } = useSendNotifications();

  const sendPush = async () => {
    await handleSendNotifications(
      checked.map((value) => value.id),
      message
    );
  };

  useEffect(() => {
    if (error) {
      Swal.fire({
        confirmButtonColor: "#001B60",
        text: t("sendNotifications.error"),
        icon: "error",
      });
    }
    if (response) {
      Swal.fire({
        confirmButtonColor: "#001B60",
        text: t("sendNotifications.success"),
        icon: "success",
      });
    }
  }, [error, response, t]);

  const onChangeSelect = (event) => {
    const selectedValues = event.target.value;
    const currentSelectedIsAll =
      selectedValues[selectedValues.length - 1] === "Todos los clubs";
    const fistSelectedIsAll = selectedValues[0] === "Todos los clubs";

    if (currentSelectedIsAll) {
      handleInputChange({
        target: { name: "clubFilter", value: ["Todos los clubs"] },
      });
    } else if (fistSelectedIsAll) {
      handleInputChange({
        target: {
          name: "clubFilter",
          value: [selectedValues[selectedValues.length - 1]],
        },
      });
    } else {
      // Actualiza el estado con los valores seleccionados
      handleInputChange({
        target: { name: "clubFilter", value: selectedValues },
      });
    }
  };

  useEffect(() => {
    setChecked(filteredUsers);
    setAddUsers([]);
    setOtherUsers(
      userList.filter(
        (user) =>
          !filteredUsers.some((filteredUser) => user.id === filteredUser.id)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveFilter, clubFilter]);

  return (
    <Stack gap={4} mt={8}>
      <Stack>
        <Typography color="primary" variant="h6">
          {t("sendNotifications.title")}
        </Typography>
        <Divider sx={{ marginTop: 2, marginBottom: 1 }} />
      </Stack>
      <Stack>
        <Typography variant="overline" color="secondary">
          {t("sendNotifications.message")}
        </Typography>
        <InputBase
          fullWidth
          rows={5}
          multiline
          name="message"
          size="small"
          variant="standard"
          onChange={handleInputChange}
          value={message}
        />
      </Stack>
      <Stack gap={2} direction="row">
        <FormControl sx={{ width: "100%" }}>
          <Select
            multiple
            size="small"
            value={clubFilter}
            name="clubFilter"
            onChange={onChangeSelect}
            renderValue={(selected) => (
              <div>
                {selected?.map((value) => (
                  <Chip key={value} label={value} sx={{ marginRight: 1 }} />
                ))}
              </div>
            )}
          >
            {[...clubsList, "Todos los clubs"].map((club) => (
              <MenuItem key={club} value={club}>
                {club}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Stack gap={2} direction="row">
        <Autocomplete
          disablePortal
          options={["Activos", "Posición activa", "Todos los socios"]}
          size="small"
          renderInput={(params) => (
            <TextField {...params} variant="outlined" name="isActiveFilter" />
          )}
          value={isActiveFilter}
          onSelect={handleInputChange}
          fullWidth
        />
      </Stack>
      <ListCheckBox
        listText={filteredUsers}
        setChecked={setChecked}
        checked={checked}
      />
      <Autocomplete
        disablePortal
        options={otherUsers}
        getOptionLabel={(option) =>
          option.name ? `${option.name} ${option.surnames}` : "Elegir inversor"
        }
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            name="isActiveFilter"
            label="Otros"
          />
        )}
        value={"Elegir inversor"}
        onChange={(event, value) => {
          if (value) {
            setChecked((prevValue) => [...prevValue, value]);
            setAddUsers((prevUsers) => [...prevUsers, value]);
            setOtherUsers((prevValue) =>
              prevValue.filter((user) => user !== value)
            );
          }
        }}
      />
      <ListCheckBox
        listText={addUsers}
        setChecked={setChecked}
        checked={checked}
      />
      <Stack alignItems="flex-end">
        <Tooltip
          title={t("sendNotifications.emptyMessage")}
          open={message === ""}
        >
          <span>
            <Button
              onClick={sendPush}
              variant="contained"
              width="250px"
              disabled={message === ""}
            >
              {t("sendNotifications.button")}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
